<script>
    const axios = require('axios');
    import { marked } from 'marked';

    export default {
        props: {
            host: {
                type: String,
                required: true,
            },
            brand: {
                type: String,
                required: true,
            },
            audience: {
                type: String,
                required: true,
            },
            language: {
                type: String,
                required: true,
            },
            title: {
                type: String,
                required: true,
            },
            logoUrl: {
                type: String,
                required: true,
            },
            openChatLabel: {
                type: String,
                required: true,
            },
            closeChatLabel: {
                type: String,
                required: true,
            },
            newConversationLabel: {
                type: String,
                required: true,
            },
            thinkingLabel: {
                type: String,
                required: true,
            },
            inputPlaceholder: {
                type: String,
                required: true,
            },
            sendLabel: {
                type: String,
                required: true,
            },
            examplesLabel: {
                type: String,
                required: true,
            },
            unreadableStreamError: {
                type: String,
                required: true,
            },
            emptyResponseLabel: {
                type: String,
                required: true,
            },
            sourcesLabel: {
                type: String,
                required: true,
            },
            networkErrorLabel: {
                type: String,
                required: true,
            },
            errorResponse401: {
                type: String,
                required: true,
            },
            errorResponse403: {
                type: String,
                required: true,
            },
            errorResponse429: {
                type: String,
                required: true,
            },
            retryAfterSecondsLabel: {
                type: String,
                required: true,
            },
            retryLaterLabel: {
                type: String,
                required: true,
            },
            genericErrorLabel: {
                type: String,
                required: true,
            },
        },
        data() {
            return {
                api: {
                    sessionEndpoint: '/session',
                    chatCompletionsEndpoint: '/chat/completions',
                },
                defaults: {
                    elapsedSeconds: '0.0',
                    responseType: 'text',
                    streamDoneToken: '[DONE]',
                    streamPrefix: 'data: ',
                },
                headers: {
                    contentType: 'Content-Type',
                    applicationJson: 'application/json',
                    retryAfter: 'retry-after',
                    endUserRef: 'X-End-User-Ref',
                },
                storage: {
                    keyPrefix: 'caa-chatbot',
                    endUserRefSuffix: 'end-user-ref',
                },
                roles: {
                    assistant: 'assistant',
                    user: 'user',
                },
                isOpen: false,
                busy: false,
                error: '',
                draft: '',
                sessionId: null,
                endUserRef: null,
                messages: [],
                thinkingStartedAt: null,
                thinkingInterval: null,
                elapsedSeconds: '0.0',
            };
        },
        computed: {
            enabled() {
                return !!this.host;
            },
            normalizedHost() {
                return this.host ? this.host.replace(/\/$/, '') : '';
            },
            storageKey() {
                return [
                    this.storage.keyPrefix,
                    this.normalizedHost,
                    this.brand,
                    this.audience,
                    this.language,
                ].join('|');
            },
            endUserRefStorageKey() {
                return [
                    this.storage.keyPrefix,
                    this.storage.endUserRefSuffix,
                    this.normalizedHost,
                    this.brand,
                    this.audience,
                ].join('|');
            },
        },
        methods: {
            replaceTokens(label, replacements = {}) {
                return Object.keys(replacements).reduce((result, key) => {
                    return result.replaceAll(`{${key}}`, replacements[key]);
                }, label || '');
            },
            getStorage() {
                try {
                    return window.sessionStorage;
                } catch (error) {
                    return null;
                }
            },
            getPersistentStorage() {
                try {
                    return window.localStorage;
                } catch (error) {
                    return this.getStorage();
                }
            },
            generateEndUserRef() {
                if (window.crypto?.randomUUID) {
                    return window.crypto.randomUUID();
                }

                return `caa-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
            },
            ensureEndUserRef() {
                if (this.endUserRef) return this.endUserRef;

                const storage = this.getPersistentStorage();
                const persistedEndUserRef = storage?.getItem(this.endUserRefStorageKey);

                if (persistedEndUserRef) {
                    this.endUserRef = persistedEndUserRef;
                    return this.endUserRef;
                }

                this.endUserRef = this.generateEndUserRef();
                storage?.setItem(this.endUserRefStorageKey, this.endUserRef);

                return this.endUserRef;
            },
            loadPersistedState() {
                this.ensureEndUserRef();

                const storage = this.getStorage();
                if (!storage || !this.storageKey) return;

                const persistedState = storage.getItem(this.storageKey);
                if (!persistedState) return;

                try {
                    const parsedState = JSON.parse(persistedState);
                    this.isOpen = parsedState.isOpen === true;
                    this.sessionId = parsedState.sessionId || null;
                    this.endUserRef = parsedState.endUserRef || this.endUserRef;
                    this.messages = Array.isArray(parsedState.messages)
                        ? parsedState.messages.map((message) => ({
                            role: message.role,
                            markdown: message.markdown || '',
                            html: this.renderMarkdown(message.markdown || ''),
                        }))
                        : [];
                } catch (error) {
                    storage.removeItem(this.storageKey);
                }
            },
            persistState() {
                const storage = this.getStorage();
                if (!storage || !this.storageKey) return;

                storage.setItem(this.storageKey, JSON.stringify({
                    isOpen: this.isOpen,
                    sessionId: this.sessionId,
                    endUserRef: this.ensureEndUserRef(),
                    messages: this.messages.map((message) => ({
                        role: message.role,
                        markdown: message.markdown,
                    })),
                }));
            },
            clearPersistedState() {
                const storage = this.getStorage();
                if (!storage || !this.storageKey) return;

                storage.removeItem(this.storageKey);
            },
            toggle() {
                this.isOpen ? this.close() : this.open();
            },
            async open() {
                this.isOpen = true;

                if (!this.sessionId && !this.messages.length) {
                    await this.startSession();
                }

                this.$nextTick(() => {
                    this.scrollToBottom();
                });

                this.persistState();
            },
            close() {
                this.isOpen = false;
                this.persistState();
            },
            async startNewConversation() {
                if (this.busy) return;

                const previousSessionId = this.sessionId;
                this.messages = [];
                this.sessionId = null;
                this.clearPersistedState();
                await this.startSession(previousSessionId);
            },
            createHeaders() {
                const endUserRef = this.ensureEndUserRef();

                return {
                    [this.headers.contentType]: this.headers.applicationJson,
                    [this.headers.endUserRef]: endUserRef,
                };
            },
            async startSession(previousSessionId = null) {
                this.error = '';
                const endUserRef = this.ensureEndUserRef();

                try {
                    const response = await axios.post(`${this.normalizedHost}${this.api.sessionEndpoint}`,
                        {
                            brand: this.brand || undefined,
                            audience: this.audience || undefined,
                            language: this.language || undefined,
                            previous_session_id: previousSessionId || undefined,
                        },
                        {
                            headers: this.createHeaders(),
                        });

                    const data = response.data;
                    this.sessionId = data.session_id;

                    let markdown = data.welcome || '';
                    if (data.suggestions?.length) {
                        markdown += `\n\n${this.examplesLabel}:\n${data.suggestions.map((suggestion) => `- ${suggestion}`).join('\n')}`;
                    }

                    if (markdown) {
                        this.messages.push({
                            role: this.roles.assistant,
                            markdown: markdown,
                            html: this.renderMarkdown(markdown),
                        });
                    }
                } catch (error) {
                    this.error = await this.getApiErrorMessage(error);
                } finally {
                    this.persistState();
                    this.$nextTick(() => {
                        this.scrollToBottom();
                    });
                }
            },
            async send() {
                const message = this.draft.trim();
                if (!message || this.busy) return;
                const endUserRef = this.ensureEndUserRef();

                if (!this.sessionId) {
                    await this.startSession();
                    if (!this.sessionId) return;
                }

                this.error = '';
                this.busy = true;
                this.draft = '';
                this.messages.push({
                    role: this.roles.user,
                    markdown: message,
                    html: this.renderMarkdown(message),
                });
                this.persistState();
                this.startThinkingTimer();

                let assistantMarkdown = '';
                const assistantMessage = {
                    role: this.roles.assistant,
                    markdown: '',
                    html: '',
                };

                this.messages.push(assistantMessage);

                try {
                    let buffer = '';
                    let processedLength = 0;
                    let xCaa = null;
                    const processBuffer = (flushRemainder = false) => {
                        const lines = buffer.split(/\r?\n/);
                        buffer = flushRemainder ? '' : (lines.pop() || '');

                        lines.forEach((line) => {
                            if (!line.startsWith(this.defaults.streamPrefix)) return;

                            const data = line.slice(this.defaults.streamPrefix.length);
                            if (data === this.defaults.streamDoneToken) return;

                            let chunk = null;
                            try {
                                chunk = JSON.parse(data);
                            } catch (error) {
                                return;
                            }

                            const token = chunk.choices?.[0]?.delta?.content;
                            if (token) {
                                assistantMarkdown += token;
                                assistantMessage.markdown = assistantMarkdown;
                                assistantMessage.html = this.renderMarkdown(assistantMarkdown);
                            }

                            if (chunk.x_caa) {
                                xCaa = chunk.x_caa;
                            }
                        });
                    };

                    const response = await axios.post(`${this.normalizedHost}${this.api.chatCompletionsEndpoint}`,
                        {
                            messages: [{ role: this.roles.user, content: message }],
                            stream: true,
                            session_id: this.sessionId,
                        },
                        {
                            headers: this.createHeaders(),
                            responseType: this.defaults.responseType,
                            onDownloadProgress: (progressEvent) => {
                                const responseText = progressEvent.event?.target?.responseText;
                                if (!responseText) return;

                                const chunkText = responseText.slice(processedLength);
                                processedLength = responseText.length;
                                buffer += chunkText;
                                processBuffer();

                                this.$forceUpdate();
                                this.$nextTick(() => {
                                    this.scrollToBottom();
                                });
                            },
                        });

                    if (typeof response.data === 'string' && response.data.length > processedLength) {
                        buffer += response.data.slice(processedLength);
                    }
                    processBuffer(true);

                    if (!assistantMarkdown && !xCaa && typeof response.data !== 'string') {
                        throw new Error(this.unreadableStreamError);
                    }

                    if (xCaa?.session_id) {
                        this.sessionId = xCaa.session_id;
                    }

                    if (xCaa?.sources?.length) {
                        assistantMarkdown += `\n\n**${this.sourcesLabel}:**\n${xCaa.sources.map((source) => `- [${source.titel || source.url}](${source.url})`).join('\n')}`;
                    }

                    if (!assistantMarkdown) {
                        assistantMarkdown = this.emptyResponseLabel;
                    }

                    assistantMessage.markdown = assistantMarkdown;
                    assistantMessage.html = this.renderMarkdown(assistantMarkdown);
                } catch (error) {
                    this.messages.pop();
                    this.error = await this.getApiErrorMessage(error);
                } finally {
                    this.stopThinkingTimer();
                    this.busy = false;
                    this.persistState();
                    this.$nextTick(() => {
                        this.scrollToBottom();
                    });
                }
            },
            renderMarkdown(markdown) {
                const html = marked.parse(markdown || '');
                const container = document.createElement('div');
                container.innerHTML = html;
                container.querySelectorAll('a').forEach((link) => {
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                });
                return container.innerHTML;
            },
            async getApiErrorMessage(error) {
                const response = error?.response;
                if (!response) {
                    return `${this.networkErrorLabel}: ${error.message || error}`;
                }

                let detail = '';

                try {
                    detail = response.data?.error?.message || '';
                } catch (error) {
                    detail = '';
                }

                switch (response.status) {
                    case 401:
                        return this.errorResponse401;
                    case 403:
                        return this.errorResponse403;
                    case 429: {
                        const retryAfterValue = response.headers?.[this.headers.retryAfter];
                        const retryAfter = retryAfterValue
                            ? this.replaceTokens(this.retryAfterSecondsLabel, { seconds: retryAfterValue })
                            : this.retryLaterLabel;
                        return this.replaceTokens(this.errorResponse429, { retryAfter });
                    }
                    default:
                        return detail || this.replaceTokens(this.genericErrorLabel, { status: response.status });
                }
            },
            startThinkingTimer() {
                this.stopThinkingTimer();
                this.thinkingStartedAt = Date.now();
                this.elapsedSeconds = this.defaults.elapsedSeconds;
                this.thinkingInterval = window.setInterval(() => {
                    this.elapsedSeconds = ((Date.now() - this.thinkingStartedAt) / 1000).toFixed(1);
                }, 100);
            },
            stopThinkingTimer() {
                if (this.thinkingInterval) {
                    window.clearInterval(this.thinkingInterval);
                    this.thinkingInterval = null;
                }
                this.thinkingStartedAt = null;
                this.elapsedSeconds = this.defaults.elapsedSeconds;
            },
            scrollToBottom() {
                if (!this.$refs.log) return;
                this.$refs.log.scrollTop = this.$refs.log.scrollHeight;
            },
        },
        mounted() {
            this.loadPersistedState();

            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },
        beforeUnmount() {
            this.persistState();
            this.stopThinkingTimer();
        },
    };
</script>

<template>
    <div v-if="enabled" class="caa-chatbot">
        <button class="caa-chatbot__launcher button is-primary is-rounded"
                type="button"
                :aria-label="isOpen ? closeChatLabel : openChatLabel"
                @click="toggle">
            <transition name="transition-fade" mode="out-in">
                <span v-if="!isOpen" key="open" class="icon is-large"><i class="fa-solid fa-xl fa-message"></i></span>
                <span v-else key="close" class="icon is-large"><i class="fa-solid fa-xl fa-times"></i></span>
            </transition>
        </button>

        <transition name="transition-slide-fade">
            <section v-if="isOpen" class="caa-chatbot__panel is-clipped is-flex is-flex-direction-column has-background-white-bis" aria-live="polite">
                <header class="caa-chatbot__header is-flex is-justify-content-space-between is-align-items-center has-background-white p-4">
                    <div class="caa-chatbot__heading is-flex is-align-items-center">
                        <img v-if="logoUrl"
                             class="caa-chatbot__logo"
                             :src="logoUrl"
                             alt=""
                             width="48"
                             height="32">
                        <strong>{{ title }}</strong>
                    </div>
                    <div class="caa-chatbot__actions is-flex is-align-items-center">
                        <button class="caa-chatbot__secondary button is-secondary is-small p-2"
                                type="button"
                                :disabled="busy"
                                @click="startNewConversation">
                            {{ newConversationLabel }}
                        </button>
                        <button type="button"
                                class="caa-chatbot__close delete"
                                :aria-label="closeChatLabel"
                                @click="close">
                        </button>
                    </div>
                </header>

                <div ref="log" class="caa-chatbot__log is-flex is-flex-direction-column p-4">
                    <article v-for="(message, index) in messages"
                             :key="`${message.role}-${index}`"
                             :class="['caa-chatbot__message', 'caa-chatbot__message--' + message.role, 'is-flex']">
                        <div class="caa-chatbot__bubble p-4">
                            <div class="content" v-html="message.html"></div>
                        </div>
                    </article>

                    <article v-if="busy" class="caa-chatbot__message caa-chatbot__message--assistant is-flex">
                        <div class="caa-chatbot__thinking is-inline-flex is-align-items-center has-background-white-ter p-4">
                            <span>{{ thinkingLabel }}</span>
                            <span>{{ elapsedSeconds }}s</span>
                        </div>
                    </article>
                </div>

                <p v-if="error" class="caa-chatbot__error has-text-danger has-text-weight-bold p-4">{{ error }}</p>

                <form class="caa-chatbot__form field has-addons has-background-white p-4" @submit.prevent="send">
                    <div class="caa-chatbot__control caa-chatbot__control--input control is-expanded">
                        <textarea v-model="draft"
                                  class="caa-chatbot__input textarea"
                                  :placeholder="inputPlaceholder"
                                  :disabled="busy"
                                  rows="1"
                                  @keydown.enter.exact.prevent="send"></textarea>
                    </div>
                    <div class="control">
                        <button class="caa-chatbot__send button is-primary"
                                type="submit"
                                :disabled="busy || !draft.trim()">
                            <!--{{ sendLabel }}-->
                            <span class="icon"><i class="fa-solid fa-paper-plane"></i></span>
                        </button>
                    </div>
                </form>
            </section>
        </transition>
    </div>
</template>
